import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import AddIcon from '@/assets/icons/add.svg?react'
import { Button, Loading, message, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'
import { MeshEndpoint } from '@/models/mesh'
import { EndpointStatus, Network } from '@/models/network'
import { formatBandwidthBit } from '@/utils/formatter'
import { cn } from '@/utils/style'

import ClusterAddInstanceModal from './ClusterAddInstanceModal'

interface ClusterDetailProps {
  network: Network
  regionDisplayName: string
  onNetworkUpdate: (network: Network) => void
}

export default function ClusterDetail({
  network,
  regionDisplayName,
  onNetworkUpdate,
}: ClusterDetailProps) {
  const [networkInstances, setNetworkInstances] = useState<MeshEndpoint[] | undefined>([])
  const [addInstanceModalOpen, setAddInstanceModalOpen] = useState<boolean>()
  const [loading, setLoading] = useState(true)
  const [handing, setHanding] = useState(false)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = 5

  const getNetworkInstances = useCallback(
    async (p = page, force: boolean = true, needNotify = false) => {
      if (force) {
        setLoading(true)
      }
      try {
        const res = await API.getMeshEndpointList({
          res_id: network.resID,
          page: p,
          per_page: pageSize,
        })
        if (res.code === 0) {
          setPage(res.pagination.page)
          setTotal(res.pagination.total)

          setNetworkInstances(res.endpoints?.map((data) => new MeshEndpoint(data)))

          const hasPendingItems = res.endpoints?.some((endpoint) => {
            return [EndpointStatus.Syncing, EndpointStatus.Removing].includes(endpoint?.status)
          })

          if (hasPendingItems) {
            setTimeout(() => {
              getNetworkInstances(p, false, true)
            }, 1000)
          } else {
            if (needNotify && res.pagination.total !== network?.capacity?.used) {
              onNetworkUpdate({
                ...network,
                capacity: { ...network?.capacity, used: res.pagination.total },
              })
            }
          }
        }
      } catch (error) {
        console.error(error)
      } finally {
        setLoading(false)
      }
    },
    [page],
  )

  useEffect(() => {
    getNetworkInstances(1, true)
  }, [network])

  const disconnetEndpointFromNetGroup = async (endpoint: MeshEndpoint) => {
    setHanding(true)
    try {
      const res = await API.deleteEndpointFromMesh({
        res_id: endpoint.resID,
      })
      if (res.code === 0) {
        message.show('success')
        getNetworkInstances(page, true, true)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setHanding(false)
    }
  }

  return (
    <>
      {loading ? (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      ) : networkInstances?.length === 0 ? (
        <EmptyView
          content={
            <span className="t-sm text-secondary">
              <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
            </span>
          }
          action={
            <Button type="secondary" size="sm" onClick={() => setAddInstanceModalOpen(true)}>
              <div className="flex gap-2 items-center">
                <AddIcon className="h-4 w-4 text-white" />
                <Trans i18nKey="mesh.addInstance">Add Instance</Trans>
              </div>
            </Button>
          }
          className="pt-0 pb-4"
        />
      ) : (
        <div>
          <table className="table">
            <thead>
              <tr>
                <th colSpan={1}>
                  <Trans i18nKey="mesh.net.tableHeaderCIDR">IP CIDR</Trans>
                </th>
                <th colSpan={1}>
                  <Trans i18nKey="mesh.net.tableHeaderID">ID</Trans>
                </th>
                <th colSpan={2}>
                  <Trans i18nKey="mesh.net.tableHeaderName">Name</Trans>
                </th>
                <th colSpan={1}>
                  <Trans i18nKey="mesh.net.tableHeaderBandwidth">Bandwidth</Trans>
                </th>
                <th colSpan={1}>
                  <Trans i18nKey="mesh.net.tableHeaderStatus">Status</Trans>
                </th>
                <th colSpan={1}>
                  <Trans i18nKey="mesh.net.tableHeaderAction">Action</Trans>
                </th>
              </tr>
            </thead>
            <tbody className="bg-panel-white">
              {networkInstances?.map((data, idx) => (
                <tr key={idx}>
                  <td colSpan={1}>{data.cidr}</td>
                  <td colSpan={1}>{data.instResID}</td>
                  <td colSpan={2}>{data.displayTitle ?? ''}</td>
                  <td colSpan={1}>
                    {formatBandwidthBit(data.endpointNodeInfo?.bandwidthBit ?? 0)}
                  </td>
                  <td colSpan={1} className={data.statusTextStyle}>
                    <Trans i18nKey={`mesh.net.status${data.statusText}`}>{data.statusText}</Trans>
                  </td>
                  <td colSpan={1}>
                    <div
                      className={cn(
                        ' text-highlight',
                        handing ? ' opacity-40 cursor-not-allowed' : 'cursor-pointer',
                      )}
                      onClick={() => disconnetEndpointFromNetGroup(data)}
                    >
                      <Trans i18nKey="mesh.net.actionDisconnect">Disconnect</Trans>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-1">
              <div
                className="flex gap-x-2 items-center justify-center text-highlight cursor-pointer px-3 py-2"
                onClick={() => setAddInstanceModalOpen(true)}
              >
                <AddIcon className="h-4 w-4" width={16} height={16} />
                <Trans i18nKey="mesh.addInstance">Add Instance</Trans>
              </div>
            </div>
            {total > 0 && (
              <Pagination
                size={pageSize}
                total={total}
                current={page}
                onChange={(page) => {
                  getNetworkInstances(page, false)
                }}
              />
            )}
          </div>
        </div>
      )}

      {addInstanceModalOpen ? (
        <ClusterAddInstanceModal
          network={network}
          regionDisplayName={regionDisplayName}
          onSuccess={() => {
            setAddInstanceModalOpen(false)
            getNetworkInstances(page, true)
          }}
          onClose={() => setAddInstanceModalOpen(false)}
        />
      ) : null}
    </>
  )
}
